/**
 * @file document-meta.tsx
 * @description Сведения карточки документа для `DocumentMetaGrid` (phase19.31,
 * часть 3): контрагент, основание, дата, сумма, НДС, подпись, шаблон,
 * создание. Плитки — данными, по две в ряд на телефоне и по четыре с 1024.
 *
 * Подписи и значения — на русском: способ подписи словами
 * (`SIGNATURE_MODE_LABEL`), деньги «1 175,00 ₽», даты «20.09.2026».
 * Контрагент — с сервера у любого статуса, с ИНН у юрлица; кто выпустил и
 * подписал — именами (контракт §19). Кто создал черновик, в документе не
 * хранится — плитка «Создан» только с датой.
 */

import { Link } from "react-router";

import { DocumentBasisLink } from "./document-cells";
import { INLINE_LINK_CLASS, documentCounterparty, documentCounterpartyInn, formatDocMoney } from "./document-view";
import type { DocumentMetaItem } from "../../ui/DocumentMetaGrid";
import { SIGNATURE_MODE_LABEL, type DocumentDto } from "../../../lib/api-crm";
import { formatShopDate, formatShopDateTime } from "../../../lib/shop-time";

interface MetaContext {
  /** База адресов магазина: `/shop/<slug>`. */
  base: string;
  canOpenOrder: boolean;
  canOpenDeal: boolean;
  canOpenCustomer: boolean;
}

/** Контрагент: имя с сервера (ссылкой на клиента — если он указан и есть право) и ИНН. */
function counterpartyValue(doc: DocumentDto, ctx: MetaContext) {
  const name = documentCounterparty(doc) ?? (doc.customerId ? "Карточка клиента" : null);
  if (!name) return "не указан";
  const inn = documentCounterpartyInn(doc);
  const title =
    doc.customerId && ctx.canOpenCustomer ? (
      <Link to={`${ctx.base}/customers/${doc.customerId}`} className={INLINE_LINK_CLASS}>
        <span className="min-w-0 break-words">{name}</span>
      </Link>
    ) : (
      name
    );
  if (!inn) return title;
  return (
    <span className="flex min-w-0 flex-col">
      <span className="min-w-0 break-words">{title}</span>
      <span className="type-caption tabular-nums text-on-surface-variant">{inn}</span>
    </span>
  );
}

/** Кто и когда: «Анна Кравец, 20.09.2026»; имени нет (удалён) — только дата. */
function byWhom(name: string | null, at: string): string {
  return [name, formatShopDate(at)].filter(Boolean).join(", ");
}

/** НДС: сумма, «без НДС» у нуля, «—» если сервер суммы не знает. */
function vatValue(doc: DocumentDto): string {
  if (doc.totalVat === null) return "—";
  return Number(doc.totalVat) === 0 ? "без НДС" : formatDocMoney(doc.totalVat, doc.currency);
}

/** Подпись: способ словами и дата; до подписи — «не подписан». */
function signatureValue(doc: DocumentDto): string {
  if (!doc.signedAt) return "не подписан";
  return `${SIGNATURE_MODE_LABEL[doc.signatureMode]}, ${formatShopDate(doc.signedAt)}`;
}

/** «Выпустил» и «Подписал» — плитки, только когда событие было. */
function peopleItems(doc: DocumentDto): DocumentMetaItem[] {
  const items: DocumentMetaItem[] = [];
  if (doc.issuedAt) items.push({ label: "Выпустил", value: byWhom(doc.issuedByName, doc.issuedAt) });
  if (doc.signedAt) items.push({ label: "Подписал", value: byWhom(doc.signedByName, doc.signedAt) });
  return items;
}

export function buildDocumentMeta(doc: DocumentDto, ctx: MetaContext): DocumentMetaItem[] {
  return [
    { label: "Контрагент", value: counterpartyValue(doc, ctx) },
    {
      label: "Сделка / заказ",
      value: <DocumentBasisLink doc={doc} base={ctx.base} canOpenOrder={ctx.canOpenOrder} canOpenDeal={ctx.canOpenDeal} wrap />,
    },
    {
      label: "Дата документа",
      value: doc.issuedAt ? formatShopDate(doc.issuedAt) : "не выпущен",
      mono: !!doc.issuedAt,
      tone: doc.issuedAt ? "default" : "warning",
    },
    { label: "Сумма", value: formatDocMoney(doc.totalGross, doc.currency), mono: true },
    { label: "НДС", value: vatValue(doc), mono: true },
    { label: "Подпись", value: signatureValue(doc), tone: doc.signedAt ? "success" : "default" },
    ...peopleItems(doc),
    // У черновика без шаблона при выпуске возьмётся шаблон вида по умолчанию.
    { label: "Шаблон", value: doc.templateName ?? (doc.status === "DRAFT" ? "по умолчанию для вида" : "не задан") },
    { label: "Создан", value: formatShopDateTime(doc.createdAt), mono: true },
  ];
}
